import { useState, useCallback, useEffect } from "react";
import { api } from "../lib/api";
import {
    SESSION_REJECT_CODE, SESSION_ACCOUNT_LOGIN_MSG,
    CHAIN_MISMATCH_CODE, CHAIN_MISMATCH_LOGIN_MSG,
    ACTIVATION_REQUIRED_CODE, ACTIVATION_LOGIN_MSG,
} from "../lib/loginErrors";
import { TOKEN_KEY, clearStoredToken, onSessionInvalidated } from "../lib/authSession";
import type { Token } from "../gen/memba/v1/memba_pb";

interface AuthState {
    token: Token | null;
    address: string;
    loading: boolean;
    error: string | null;
}

// Exported for round-trip tests. MUST persist every field the backend signs
// over (nonce, userAddress, expiration, chainId) — the REST endpoints send this
// stored JSON verbatim as a Bearer token, and the server re-verifies the
// signature over the reconstructed proto. Dropping any signed field (chainId was
// added by AUTH-CHAINID-01) makes the recomputed signature mismatch → 401.
export function saveToken(token: Token) {
    try {
        localStorage.setItem(TOKEN_KEY, JSON.stringify({
            nonce: token.nonce,
            userAddress: token.userAddress,
            expiration: token.expiration,
            chainId: token.chainId,
            serverSignature: token.serverSignature,
        }));
    } catch { /* localStorage unavailable */ }
}

export function loadToken(): Token | null {
    try {
        const raw = localStorage.getItem(TOKEN_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);

        // F1: Validate expiry before rehydrating.
        // P2-D: Client-side validation only checks expiry, not server signature.
        // This is acceptable because the backend re-validates the full token
        // (including server signature) on every RPC call. A forged token in
        // localStorage will simply cause 401 errors on the first API call.
        if (data.expiration) {
            const exp = new Date(data.expiration);
            if (exp <= new Date()) {
                localStorage.removeItem(TOKEN_KEY);
                return null;
            }
        }
        return data as Token;
    } catch {
        return null;
    }
}

const clearToken = clearStoredToken;

export function useAuth() {
    const [state, setState] = useState<AuthState>(() => {
        const saved = loadToken();
        return {
            token: saved,
            address: saved?.userAddress || "",
            loading: false,
            error: null,
        };
    });

    // Recheck token expiry periodically.
    useEffect(() => {
        if (!state.token) return;
        const checkExpiry = () => {
            if (state.token?.expiration) {
                const exp = new Date(state.token.expiration);
                if (exp <= new Date()) {
                    clearToken();
                    setState({ token: null, address: "", loading: false, error: "Session expired" });
                }
            }
        };
        const timer = setInterval(checkExpiry, 60_000);
        return () => clearInterval(timer);
    }, [state.token]);

    // F-29 self-heal: react to the SERVER rejecting the token, not just to
    // expiry. The interval above is the only thing that used to clear a
    // session, so a token the backend refuses (wrong chain, rotated key)
    // survived until its natural expiry while every call 401'd behind a UI
    // that still looked signed in. Subscribing unconditionally — not gated on
    // state.token — because the stored token can be dropped by the interceptor
    // before this effect re-runs.
    useEffect(() => onSessionInvalidated((reason) => {
        setState({ token: null, address: "", loading: false, error: reason });
    }), []);

    const getChallenge = useCallback(async (userPubkeyJson?: string, chainId?: string) => {
        // AUTH-CHAINID-01: bind the challenge to the active chain so the server signs
        // chain_id into it; the client must echo that chain_id back (buildTokenRequestInfo).
        const res = await api.getChallenge({ userPubkeyJson: userPubkeyJson || "", chainId: chainId || "" });
        return res.challenge;
    }, []);

    const getToken = useCallback(
        async (infoJson: string, userSignature: string) => {
            setState((s) => ({ ...s, loading: true, error: null }));
            try {
                const res = await api.getToken({ infoJson, userSignature });
                const token = res.authToken;
                if (token) {
                    saveToken(token);
                    setState({
                        token,
                        address: token.userAddress,
                        loading: false,
                        error: null,
                    });
                }
                return token;
            } catch (err) {
                const message = err instanceof Error ? err.message : "Auth failed";
                // AUTH-SESSION-REJECT-01 rides the wire as a bare code (backend
                // tokenDenied) — rethrow the human mapping so every sign-in
                // surface shows real guidance. All other failures keep the
                // null-return contract existing callers rely on.
                if (message.includes(SESSION_REJECT_CODE)) {
                    setState((s) => ({ ...s, loading: false, error: SESSION_ACCOUNT_LOGIN_MSG }));
                    throw new Error(SESSION_ACCOUNT_LOGIN_MSG);
                }
                // F-29: the server refused to mint for this chain. Surfaced the
                // same way, because it is equally actionable — the user can fix
                // it by switching network. Previously this request SUCCEEDED and
                // the damage only appeared later, on every subsequent call.
                if (message.includes(CHAIN_MISMATCH_CODE)) {
                    setState((s) => ({ ...s, loading: false, error: CHAIN_MISMATCH_LOGIN_MSG }));
                    throw new Error(CHAIN_MISMATCH_LOGIN_MSG);
                }
                // AUTH-ACTIVATE-01: untransacted wallet on an enforced-auth
                // chain. Rethrown WITH the code kept in the message so the
                // caller (Layout) can open the Activate-wallet flow, not just
                // show prose. Same actionability bar as the two above.
                if (message.includes(ACTIVATION_REQUIRED_CODE)) {
                    setState((s) => ({ ...s, loading: false, error: ACTIVATION_LOGIN_MSG }));
                    throw new Error(`${ACTIVATION_LOGIN_MSG} (${ACTIVATION_REQUIRED_CODE})`);
                }
                setState((s) => ({ ...s, loading: false, error: message }));
                return null;
            }
        },
        []
    );

    const logout = useCallback(() => {
        clearToken();
        setState({ token: null, address: "", loading: false, error: null });
    }, []);

    return {
        ...state,
        isAuthenticated: !!state.token,
        getChallenge,
        getToken,
        logout,
    };
}
